import {
  ExperienceAnchor,
  ExperienceAnchorType,
  IItemTemplateTimingFixed,
  IItemTemplateTimingFixedTask
} from '@dadohr/shared/domainTypes/experience';
import moment from 'moment-timezone';
import { UnsafeTFunction } from '../../i18next';
import { HUMAN_DATE_SHORT, ONE_DAY, Timestamp, toMoment } from '../time';

/**
 * How a fixed-time item's day was expressed by the author. Only written on
 * milestone experiences; `start` stays the source of truth and the backend never
 * reads this field.
 */
export type FixedTimingAnchorMode = 'expStart' | 'beforeAnchor' | 'afterAnchor';

export type IItemTemplateTimingFixedWithAnchorMode =
  IItemTemplateTimingFixed & {
    anchorMode?: FixedTimingAnchorMode;
  };

export const DEFAULT_ANCHOR_MODE: FixedTimingAnchorMode = 'expStart';

const msToDays = (ms: number) => Math.round(ms / ONE_DAY);

/**
 * Days between the experience start and the milestone (negative = experience
 * starts before the milestone). `null` when the experience has no milestone or
 * uses a calendar-relative offset.
 */
export const getMilestoneOffsetDays = (
  anchor: ExperienceAnchor
): number | null => {
  if (anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE) return null;
  if (anchor.offset.type !== 'ABSOLUTE') return null;
  return msToDays(anchor.offset.duration);
};

/** "before milestone" is only reachable when the experience starts before it. */
export const getAvailableAnchorModes = (
  offsetDays: number
): FixedTimingAnchorMode[] =>
  offsetDays < 0
    ? ['expStart', 'beforeAnchor', 'afterAnchor']
    : ['expStart', 'afterAnchor'];

/**
 * A saved mode that is no longer available (the experience's offset changed
 * sign) is left in the doc until the next edit; every reader derives from here,
 * so the stale value is inert.
 */
/**
 * A change to the experience's offset can move a day to the other side of the
 * milestone; show it from that side rather than as a negative count. Modes other
 * than the two milestone ones pass through unchanged.
 */
const flipAcrossMilestone = <M extends string>(
  mode: M,
  dayMs: number,
  offsetDays: number
): M | 'beforeAnchor' | 'afterAnchor' => {
  const day = msToDays(dayMs);
  if (mode === 'afterAnchor' && day + offsetDays < 0) return 'beforeAnchor';
  if (mode === 'beforeAnchor' && -offsetDays - day < 1) return 'afterAnchor';
  return mode;
};

export const getEffectiveAnchorMode = (
  timing: IItemTemplateTimingFixedWithAnchorMode,
  offsetDays: number
): FixedTimingAnchorMode => {
  const saved = timing.anchorMode ?? DEFAULT_ANCHOR_MODE;
  const mode = getAvailableAnchorModes(offsetDays).includes(saved)
    ? saved
    : DEFAULT_ANCHOR_MODE;
  return flipAcrossMilestone(mode, timing.start, offsetDays);
};

/**
 * Bounds of the number field so that `start` stays >= 0. "before" counts down
 * from the offset; "after" counts up from the milestone, which is only after
 * day 0 when the experience starts after the milestone.
 */
export const getDisplayDaysBounds = (
  mode: FixedTimingAnchorMode,
  offsetDays: number
): { min: number; max: number | null } => {
  switch (mode) {
    case 'expStart':
      return { min: 0, max: null };
    case 'beforeAnchor':
      return { min: 1, max: Math.max(0, -offsetDays) };
    case 'afterAnchor':
      return { min: Math.max(0, offsetDays), max: null };
  }
};

export const fixedStartToDisplayDays = (
  start: number,
  mode: FixedTimingAnchorMode,
  offsetDays: number
): number => {
  const startDays = msToDays(start);
  switch (mode) {
    case 'expStart':
      return startDays;
    case 'beforeAnchor':
      return -offsetDays - startDays;
    case 'afterAnchor':
      return startDays + offsetDays;
  }
};

export const displayDaysToFixedStart = (
  days: number,
  mode: FixedTimingAnchorMode,
  offsetDays: number
): number => {
  switch (mode) {
    case 'expStart':
      return days * ONE_DAY;
    case 'beforeAnchor':
      return (-offsetDays - days) * ONE_DAY;
    case 'afterAnchor':
      return (days - offsetDays) * ONE_DAY;
  }
};

/**
 * Parses what a number input can still hold: letters are blocked, but a minus
 * sign, decimal point, exponent and empty string are not. Returns `null` when
 * nothing usable was typed.
 */
export const parseDisplayDays = (raw: string): number | null => {
  const n = Number(raw.trim());
  if (raw.trim() === '' || Number.isNaN(n)) return null;
  return Math.floor(Math.abs(n));
};

const startOfDayInTz = (ts: Timestamp, tz: string) =>
  toMoment(ts).tz(tz).startOf('day');

/** Calendar date of a fixed-time item on a fixed-date experience. */
export const getFixedDateForStart = (
  anchorTs: Timestamp,
  start: number,
  tz: string
): moment.Moment => startOfDayInTz(anchorTs, tz).add(msToDays(start), 'days');

/**
 * `start` for a picked calendar date on a fixed-date experience, in whole days.
 * The picked moment is read by its calendar fields (not its instant), so it
 * doesn't matter which timezone the picker built it in.
 */
export const getStartForFixedDate = (
  anchorTs: Timestamp,
  date: moment.Moment,
  tz: string
): number => {
  const anchorDay = startOfDayInTz(anchorTs, tz);
  const pickedDay = moment.tz(
    { year: date.year(), month: date.month(), date: date.date() },
    tz
  );
  return pickedDay.diff(anchorDay, 'days') * ONE_DAY;
};

const daysPhrase = (days: number, t: UnsafeTFunction) =>
  days === 1 ? t('1 day') : t('{{days}} days', { days });

/** "N days before/after {milestone}", or "On {milestone}" for zero days after. */
const milestonePhrase = (
  mode: 'beforeAnchor' | 'afterAnchor',
  days: number,
  milestoneLabel: string | null,
  t: UnsafeTFunction
) => {
  const milestone = milestoneLabel ?? t('milestone');
  if (mode === 'beforeAnchor') {
    return t('{{days}} before {{milestone}}', {
      days: daysPhrase(days, t),
      milestone
    });
  }
  return days === 0
    ? t('On {{milestone}}', { milestone })
    : t('{{days}} after {{milestone}}', {
        days: daysPhrase(days, t),
        milestone
      });
};

/**
 * Markdown summary line for a fixed-time item in the new UX, in the mode the
 * author chose. `milestoneLabel` is the milestone's short label; only needed for
 * milestone experiences.
 */
export const getFixedTimingSummaryText = (
  timing: IItemTemplateTimingFixedWithAnchorMode,
  anchor: ExperienceAnchor,
  tz: string,
  milestoneLabel: string | null,
  t: UnsafeTFunction
): string => {
  if (anchor.type === ExperienceAnchorType.FIXED) {
    return t('{{date}} (day {{day}} of experience)', {
      date: getFixedDateForStart(anchor.ts, timing.start, tz).format(
        HUMAN_DATE_SHORT
      ),
      day: msToDays(timing.start) + 1
    });
  }
  const offsetDays = getMilestoneOffsetDays(anchor);
  if (offsetDays === null) {
    return t('Day {{ day }} of experience', {
      day: msToDays(timing.start) + 1
    });
  }
  const mode = getEffectiveAnchorMode(timing, offsetDays);
  const days = fixedStartToDisplayDays(timing.start, mode, offsetDays);
  if (mode === 'expStart') {
    return days === 0
      ? t('On day 1 of experience')
      : t('{{days}} after experience starts', {
          days: daysPhrase(days, t)
        });
  }
  return milestonePhrase(mode, days, milestoneLabel, t);
};

/* ------------------------------------------------------------ task deadlines */

/**
 * How a fixed task's deadline was expressed by the author. `taskStart` is a
 * plain duration from the task's start ("N days after task start"); the
 * milestone modes pin the deadline to the milestone so it no longer moves with
 * the start. Only written on milestone experiences; `end` stays the source of
 * truth.
 */
export type DeadlineAnchorMode = 'taskStart' | 'beforeAnchor' | 'afterAnchor';

export type IItemTemplateTimingFixedTaskWithModes =
  IItemTemplateTimingFixedTask &
    IItemTemplateTimingFixedWithAnchorMode & {
      deadlineAnchorMode?: DeadlineAnchorMode;
    };

export const DEFAULT_DEADLINE_MODE: DeadlineAnchorMode = 'taskStart';

/** A deadline must be at least this long after the task's start. */
export const MIN_TASK_DURATION = ONE_DAY;

export const isDeadlineTooEarly = (start: number, end: number) =>
  end - start < MIN_TASK_DURATION;

/** The milestone modes are offered whenever the experience starts before the milestone. */
export const getAvailableDeadlineModes = (
  offsetDays: number
): DeadlineAnchorMode[] =>
  offsetDays < 0
    ? ['taskStart', 'beforeAnchor', 'afterAnchor']
    : ['taskStart', 'afterAnchor'];

export const getEffectiveDeadlineMode = (
  timing: IItemTemplateTimingFixedTaskWithModes,
  offsetDays: number
): DeadlineAnchorMode => {
  const saved = timing.deadlineAnchorMode ?? DEFAULT_DEADLINE_MODE;
  const mode = getAvailableDeadlineModes(offsetDays).includes(saved)
    ? saved
    : DEFAULT_DEADLINE_MODE;
  return flipAcrossMilestone(mode, timing.end, offsetDays);
};

export const fixedEndToDisplayDays = (
  end: number,
  start: number,
  mode: DeadlineAnchorMode,
  offsetDays: number
): number =>
  mode === 'taskStart'
    ? msToDays(end) - msToDays(start)
    : fixedStartToDisplayDays(end, mode, offsetDays);

export const displayDaysToFixedEnd = (
  days: number,
  start: number,
  mode: DeadlineAnchorMode,
  offsetDays: number
): number =>
  mode === 'taskStart'
    ? start + days * ONE_DAY
    : displayDaysToFixedStart(days, mode, offsetDays);

/**
 * True when a fixed task's deadline is pinned (milestone mode or calendar
 * date), so it stays put when the start moves; otherwise it is a duration and
 * follows the start. Only the NEW_UX deadline field can show a pinned deadline;
 * the classic field always edits a duration, so classic never pins.
 */
export const isDeadlinePinned = (
  timing: IItemTemplateTimingFixedTaskWithModes,
  anchor: ExperienceAnchor,
  newUx: boolean
) => {
  if (!newUx) return false;
  if (anchor.type === ExperienceAnchorType.FIXED) return true;
  const offsetDays = getMilestoneOffsetDays(anchor);
  return (
    offsetDays !== null &&
    getEffectiveDeadlineMode(timing, offsetDays) !== 'taskStart'
  );
};

/**
 * Number-field bounds per mode, all derived from the one rule
 * `end - start >= MIN_TASK_DURATION`: the earliest allowed `end`, expressed in
 * the mode's units. `max` is null when unbounded. In "before" mode a max below
 * `min` means no valid value exists; the field then always errors.
 */
export const getDeadlineDaysBounds = (
  mode: DeadlineAnchorMode,
  start: number,
  offsetDays: number
): { min: number; max: number | null } => {
  const earliestEnd = start + MIN_TASK_DURATION;
  switch (mode) {
    case 'taskStart':
      return {
        min: fixedEndToDisplayDays(earliestEnd, start, mode, offsetDays),
        max: null
      };
    case 'beforeAnchor':
      // Counting down towards the milestone, the earliest end is the largest count.
      return {
        min: 1,
        max: fixedEndToDisplayDays(earliestEnd, start, mode, offsetDays)
      };
    case 'afterAnchor':
      return {
        min: Math.max(
          0,
          fixedEndToDisplayDays(earliestEnd, start, mode, offsetDays)
        ),
        max: null
      };
  }
};

/**
 * Summary line for a fixed task's deadline in the new UX. Returns a string
 * whenever the deadline has a milestone or calendar-date phrasing; returns null
 * when it does not (mode `taskStart`, or an experience with no milestone), and
 * the caller renders the plain duration instead.
 */
export const getFixedDeadlineSummaryText = (
  timing: IItemTemplateTimingFixedTaskWithModes,
  anchor: ExperienceAnchor,
  tz: string,
  milestoneLabel: string | null,
  t: UnsafeTFunction
): string | null => {
  if (anchor.type === ExperienceAnchorType.FIXED) {
    return t('{{date}} ({{days}} after task start)', {
      date: getFixedDateForStart(anchor.ts, timing.end, tz).format(
        HUMAN_DATE_SHORT
      ),
      days: daysPhrase(msToDays(timing.end) - msToDays(timing.start), t)
    });
  }
  const offsetDays = getMilestoneOffsetDays(anchor);
  if (offsetDays === null) return null;
  const mode = getEffectiveDeadlineMode(timing, offsetDays);
  if (mode === 'taskStart') return null;
  const days = fixedEndToDisplayDays(
    timing.end,
    timing.start,
    mode,
    offsetDays
  );
  return milestonePhrase(mode, days, milestoneLabel, t);
};
